import { Flex, Box, Heading, Text, Button } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { ReloadIcon } from "@/assets/icons";
import type { ErrorComponentProps } from "@tanstack/react-router";

type ErrorOrNotFoundPageProps = {
  title: string;
  error?: Error;
  info?: ErrorComponentProps["info"];
  reset?: ErrorComponentProps["reset"];
  showReset?: boolean;
};

const ErrorOrNotFoundPage = ({
  title,
  error: _error,
  info: _info,
  reset,
  showReset = true,
}: ErrorOrNotFoundPageProps) => {
  const showError =
    import.meta.env.VITE_DEVELOPMENT_MODE === "true" && !!_error;

  return (
    <>
      {showError && (
        <Box className="bg-red-100 text-red-800 p-4 mt-8 rounded-md mx-4">
          <Heading>Error</Heading>
          <pre>{_error.stack}</pre>

          {_info?.componentStack && (
            <Box mt="2">
              <Heading size="4">Component Stack</Heading>
              <pre>{_info.componentStack}</pre>
            </Box>
          )}
        </Box>
      )}
      <Flex
        direction={{ initial: "column", md: "row" }}
        gap={{ initial: "4", md: "8" }}
        px={{ initial: "4" }}
        mt="7"
        align="center"
        justify="center"
      >
        <Flex direction="column" gap="3">
          <Heading
            data-testid="error-page-title"
            className="text-[48px] font-semibold leading-[110%] tracking-[-1.44px]"
          >
            {title}
          </Heading>
          <Text className="leading-[140%] text-gray-700">
            No worries, you can still have fun
          </Text>
          <Flex direction="row" gap="2" mt="3" align="center">
            <Link to="/">
              <Button
                variant="solid"
                size="2"
                radius="full"
                className="bg-black px-4 cursor-pointer py-5 md:py-0"
              >
                Check out our Features
              </Button>
            </Link>
            <Link to="/marketplace">
              <Button
                variant="solid"
                size="2"
                radius="full"
                className="bg-black px-4 cursor-pointer py-5 md:py-0"
              >
                Visit the Marketplace
              </Button>
            </Link>
            {showReset && reset && (
              <Button
                onClick={reset}
                variant="ghost"
                size="1"
                className="mx-1 cursor-pointer"
              >
                <ReloadIcon />
              </Button>
            )}
          </Flex>
        </Flex>
        <Box>
          <img src="/tape.png" alt="Tape" />
        </Box>
      </Flex>
    </>
  );
};

export default ErrorOrNotFoundPage;
